import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote, Globe, Sparkles, BadgeCheck } from 'lucide-react';
import { Testimonial, TestimonialsConfig } from '../types';
import { TrustpilotBadge } from './TrustpilotBadge';

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
  config: TestimonialsConfig;
  darkMode?: boolean;
}

// Helper for premium highlighting
const formatReviewText = (text: string) => {
  const highlightWords = ['RH Editography', 'fast and accurate', 'highly recommend', 'excellent', 'perfect', 'amazing', 'best'];
  const regex = new RegExp(`(${highlightWords.join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) => {
    if (highlightWords.some(w => w.toLowerCase() === part.toLowerCase())) {
      return <span key={i} className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent font-bold">{part}</span>;
    }
    return part;
  });
};

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({
  testimonials,
  config,
  darkMode = true,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);

  const visibleTestimonials = testimonials
    .filter(t => t.isVisible !== false)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  const nextTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % visibleTestimonials.length);
    setAnimationKey(prev => prev + 1);
  }, [visibleTestimonials.length]);

  const prevTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + visibleTestimonials.length) % visibleTestimonials.length);
    setAnimationKey(prev => prev + 1);
  }, [visibleTestimonials.length]);

  useEffect(() => {
    if (isHovered || visibleTestimonials.length <= 1) return;
    const interval = setInterval(nextTestimonial, 6000);
    return () => clearInterval(interval);
  }, [isHovered, nextTestimonial, visibleTestimonials.length]);

  if (visibleTestimonials.length === 0) return null;

  const item = visibleTestimonials[currentIndex];

  return (
    <section className="py-20 relative overflow-hidden z-10 border-t border-slate-800/80 bg-slate-950/95" id="testimonials">
      {/* Background Glow Accents */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-16">
        
        {/* Top Header & Trustpilot Rating Badge */}
        <div className="text-center space-y-5 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-black uppercase tracking-wider shadow-sm">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Verified Customer Reviews</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
            Trusted by Designers & Agencies <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">Worldwide</span>
          </h2>
          {/* Trustpilot Rating Badge */}
          {config.showTrustpilotWidget !== false && (
            <div className="pt-2">
              <TrustpilotBadge config={config} darkMode={darkMode} />
            </div>
          )}
        </div>

        {/* Single Large Testimonial Carousel */}
        <div 
          className="relative max-w-4xl mx-auto"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          
          {/* Desktop Navigation Arrows (Outside) */}
          <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 -left-16 -right-16 justify-between pointer-events-none z-20">
            <button 
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full bg-slate-900 border border-slate-700/50 hover:bg-slate-800 hover:border-cyan-500/50 text-slate-400 hover:text-white flex items-center justify-center shadow-xl transition-all pointer-events-auto"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full bg-slate-900 border border-slate-700/50 hover:bg-slate-800 hover:border-cyan-500/50 text-slate-400 hover:text-white flex items-center justify-center shadow-xl transition-all pointer-events-auto"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* The Card */}
          <div className="relative p-[1px] rounded-3xl bg-gradient-to-br from-cyan-500/30 via-transparent to-purple-500/30 shadow-[0_0_40px_rgba(139,92,246,0.1)] group">
            <div className="relative w-full bg-[#0b1120] rounded-[23px] overflow-hidden p-6 md:p-10">
              
              {/* Subtle Background Glow inside card */}
              <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none -translate-y-1/2 translate-x-1/4"></div>
              
              {/* Content Container (Animated) */}
              <div key={animationKey} className="animate-fade-slide grid grid-cols-1 md:grid-cols-[1fr_240px] gap-6 md:gap-12 relative z-10 items-center">
                
                {/* Left Side: Text and User */}
                <div className="space-y-6">
                  
                  {/* Stars */}
                  <div className="flex items-center gap-1.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 sm:w-5 sm:h-5 ${
                          star <= item.starRating
                            ? 'fill-amber-400 text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]'
                            : 'text-slate-700'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Review Text */}
                  <div className="relative">
                    <span className="absolute -top-3 -left-6 text-indigo-500/40 hidden sm:block">
                      <Quote className="w-6 h-6 fill-indigo-500/20 rotate-180" />
                    </span>
                    <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal relative z-10 tracking-wide">
                      {formatReviewText(item.reviewText)}
                    </p>
                  </div>

                  {/* Subtle Divider */}
                  <div className="w-32 h-[1px] bg-gradient-to-r from-slate-700/50 to-transparent my-6"></div>

                  {/* User Profile */}
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {item.customerPhoto ? (
                        <img
                          src={item.customerPhoto}
                          alt={item.customerName}
                          className="w-12 h-12 rounded-full object-cover border border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.2)]"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.customerName)}&background=8b5cf6&color=fff&bold=true`;
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-blue-700 flex items-center justify-center text-white font-black text-lg border border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.2)]">
                          {item.customerName.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <h4 className="text-base font-medium text-white flex items-center gap-1.5">
                        {item.customerName}
                        {item.verifiedCustomer !== false && (
                          <BadgeCheck className="w-4 h-4 text-[#0b1120] fill-cyan-400" />
                        )}
                      </h4>
                      {item.country && (
                        <p className="text-xs font-normal text-slate-400 flex items-center gap-1 mt-0.5">
                          <Globe className="w-3 h-3 text-slate-500" />
                          <span>{item.country}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Side: Large Glowing Quote Icon */}
                <div className="hidden md:flex items-center justify-center h-full">
                  <div className="relative w-48 h-48 flex items-center justify-center">
                    {/* Thin elegant dotted ring */}
                    <div className="absolute inset-0 rounded-full border border-dashed border-slate-700/60 animate-[spin_60s_linear_infinite]"></div>
                    <div className="absolute inset-2 rounded-full border border-dashed border-slate-700/30 animate-[spin_40s_linear_infinite_reverse]"></div>
                    
                    {/* Soft ambient center glow */}
                    <div className="absolute inset-4 bg-indigo-500/20 rounded-full blur-2xl"></div>
                    
                    {/* Premium Quote Icon */}
                    <Quote className="w-20 h-20 text-transparent fill-[url(#quote-gradient)] rotate-180 drop-shadow-[0_0_16px_rgba(99,102,241,0.6)] relative z-10" />
                    
                    {/* SVG Gradient Definition for the icon */}
                    <svg width="0" height="0">
                      <linearGradient id="quote-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop stopColor="#3b82f6" offset="0%" />
                        <stop stopColor="#8b5cf6" offset="100%" />
                      </linearGradient>
                    </svg>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Mobile Navigation Controls & Pagination */}
          <div className="mt-8 flex flex-col items-center gap-6">
            
            {/* Mobile Arrows */}
            <div className="flex md:hidden items-center gap-4">
              <button 
                onClick={prevTestimonial}
                className="w-10 h-10 rounded-full bg-slate-900 border border-slate-700/50 flex items-center justify-center text-slate-400 active:scale-95 transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={nextTestimonial}
                className="w-10 h-10 rounded-full bg-slate-900 border border-slate-700/50 flex items-center justify-center text-slate-400 active:scale-95 transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Pagination Dots */}
            <div className="flex items-center justify-center gap-2.5">
              {visibleTestimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentIndex(idx);
                    setAnimationKey(prev => prev + 1);
                  }}
                  className={`transition-all duration-300 rounded-full ${
                    currentIndex === idx 
                      ? 'w-3 h-3 bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.8)]' 
                      : 'w-2 h-2 bg-slate-700 hover:bg-slate-500'
                  }`}
                  aria-label={`Go to testimonial ${idx + 1}`}
                />
              ))}
            </div>

          </div>

        </div>
      </div>
    </section>
  );
};
